import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "form-manufacturer-related" }

import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { manufacturerList } from "@/common/api/rest/manufacturer";
import {
  manufacturerRelatedList,
  manufacturerRelatedCreate,
  manufacturerRelatedDelete,
} from "@/common/api/rest/manufacturer-related";

import type { ManufacturerRelatedType } from "@/common/types/manufacturer-related";

import { baseMultiselect } from "@/common/components";

const OPTIONS_LIMIT = 20;


export default /*@__PURE__*/_defineComponent({
  __name: 'form-manufacturer-related',
  props: {
    manufacturerId: {}
  },
  setup(__props: any) {

const { t } = useI18n();

const props = __props;

const options = ref<{ option: string; value: string }[]>([]);
const relations = ref<ManufacturerRelatedType[]>([]);

const page = ref(0);
const pages = ref(0);
const search = ref("");
const loadingOptions = ref(false);

const predecessor = ref<string[]>([]);
const successor = ref<string[]>([]);
const subsidiary = ref<string[]>([]);

const selectedOptions = computed(() =>
  relations.value
    .filter((item) => item.manufacturer_id !== props.manufacturerId)
    .map((item) => ({
      option: item.name ?? item.manufacturer_id,
      value: item.manufacturer_id,
    })),
);

const displayOptions = computed(() => {
  const map = new Map<string, { option: string; value: string }>();

  selectedOptions.value.forEach((item) => map.set(item.value, item));
  options.value.forEach((item) => map.set(item.value, item));

  return Array.from(map.values());
});

function syncFromRelations() {
  predecessor.value = relations.value
    .filter((item) => item.type === "PREDECESSOR")
    .map((item) => item.manufacturer_id);

  successor.value = relations.value
    .filter((item) => item.type === "SUCCESSOR")
    .map((item) => item.manufacturer_id);

  subsidiary.value = relations.value
    .filter((item) => item.type === "SUBSIDIARY")
    .map((item) => item.manufacturer_id);
}

let optionsRequestId = 0;

async function fetchOptions(append = false) {
  if (append && loadingOptions.value) return;
  if (append && page.value + 1 >= pages.value) return;

  const pageToFetch = append ? page.value + 1 : 0;
  const requestId = ++optionsRequestId;

  loadingOptions.value = true;

  try {
    const response = await manufacturerList({
      page: pageToFetch,
      limit: OPTIONS_LIMIT,
      search: search.value,
      sort: "ASC",
      sort_by: "name",
    });

    if (requestId !== optionsRequestId) return;

    const { success, data, message } = response;

    if (success) {
      const list = (data.list ?? [])
        .filter((item: any) => item.manufacturer_id !== props.manufacturerId)
        .map((item: any) => ({
          option: item.name,
          value: item.manufacturer_id,
        }));

      options.value = append ? [...options.value, ...list] : list;
      page.value = pageToFetch;
      pages.value = data.infos?.pages ?? 0;
    } else {
      toast({ message: message, type: "error" });
    }
  } catch {
    toast({ message: t("error.default"), type: "error" });
  } finally {
    if (requestId === optionsRequestId) loadingOptions.value = false;
  }
}

const debouncedSearch = debounce((value: string) => {
  search.value = value.trim();
  page.value = 0;
  pages.value = 0;
  options.value = [];

  fetchOptions();
}, 400);

function onSearch(value: string) {
  debouncedSearch(value ?? "");
}

function onScrollEnd() {
  fetchOptions(true);
}

async function fetchRelations() {
  await manufacturerRelatedList(props.manufacturerId, {
    page: 0,
    limit: 100,
    sort: "ASC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        relations.value = data.list;
        syncFromRelations();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function sync(type: string, selected: string[]) {
  if (!props.manufacturerId) return;

  const current = relations.value.filter((item) => item.type === type);
  const currentIds = current.map((item) => item.manufacturer_id);

  const toAdd = selected.filter((id) => !currentIds.includes(id));
  const toRemove = current.filter(
    (item) => !selected.includes(item.manufacturer_id),
  );

  if (!toAdd.length && !toRemove.length) return;

  store.commit("SET_LOADING", true);

  try {
    for (const id of toAdd) {
      await manufacturerRelatedCreate(props.manufacturerId, {
        manufacturer_id: id,
        type,
      });
    }

    for (const item of toRemove) {
      await manufacturerRelatedDelete(item.item_id);
    }
  } catch {
    toast({ message: t("error.default"), type: "error" });
  } finally {
    await fetchRelations();
    store.commit("SET_LOADING", false);
  }
}

onMounted(async () => {
  await Promise.all([fetchOptions(), fetchRelations()]);
});

onBeforeUnmount(() => {
  debouncedSearch.cancel();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createVNode(_unref(baseMultiselect), {
      value: predecessor.value,
      options: displayOptions.value,
      "is-empty": false,
      remote: "",
      "filter-placeholder": _unref(t)('common.labels.search'),
      onChange: _cache[0] || (_cache[0] = ($event: any) => (sync('PREDECESSOR', $event))),
      onSearch: onSearch,
      onScrollEnd: onScrollEnd
    }, {
      label: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)("manufacturer.labels.predecessor")), 1)
      ]),
      _: 1
    }, 8, ["value", "options", "filter-placeholder"]),
    _createVNode(_unref(baseMultiselect), {
      value: successor.value,
      options: displayOptions.value,
      "is-empty": false,
      remote: "",
      "filter-placeholder": _unref(t)('common.labels.search'),
      onChange: _cache[1] || (_cache[1] = ($event: any) => (sync('SUCCESSOR', $event))),
      onSearch: onSearch,
      onScrollEnd: onScrollEnd
    }, {
      label: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)("manufacturer.labels.successor")), 1)
      ]),
      _: 1
    }, 8, ["value", "options", "filter-placeholder"]),
    _createVNode(_unref(baseMultiselect), {
      value: subsidiary.value,
      options: displayOptions.value,
      "is-empty": false,
      remote: "",
      "filter-placeholder": _unref(t)('common.labels.search'),
      onChange: _cache[2] || (_cache[2] = ($event: any) => (sync('SUBSIDIARY', $event))),
      onSearch: onSearch,
      onScrollEnd: onScrollEnd
    }, {
      label: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(t)("manufacturer.labels.subsidiary")), 1)
      ]),
      _: 1
    }, 8, ["value", "options", "filter-placeholder"])
  ]))
}
}

})